import { strIsNullOrEmpty } from '@fox/atoms';
import { sendUpdate } from '@fox/extensions';
import { createContext, useContext, useRef, useState } from 'react';
import { DelegateCapInfo, EntityInfo, Relation, RepresentativeUnit } from './contracts';
import { representativeEntityId } from './util';

export type RepresentativesDataCache = {
	sourceData: Record<string, { data: EntityInfo[]; totalResults: number; sourceDelegateCaps?: DelegateCapInfo[] }>;
	targetData: Record<string, { data: EntityInfo[]; totalResults: number }>;
};

export type RepresentativesContextProps = {
	unit: RepresentativeUnit;
	children: React.ReactNode;
};
export type RepresentativesContext = {
	unit: RepresentativeUnit;
	getBasePosition: (position: string) => string;
	// Data cache access with smart updates
	getSourceData: (sourceId: string) => { data: EntityInfo[]; totalResults: number; sourceDelegateCaps?: DelegateCapInfo[] } | undefined;
	getTargetData: (targetId: string) => { data: EntityInfo[]; totalResults: number } | undefined;
	updateSourceData: (sourceId: string, data: { data: EntityInfo[]; totalResults: number; sourceDelegateCaps?: DelegateCapInfo[] }) => boolean;
	updateTargetData: (targetId: string, data: { data: EntityInfo[]; totalResults: number }) => boolean;
	// Optimistic updates
	optimisticallyAddRelation: (personId: string, relation: Relation, sourceId: string, targetId: string) => void;
	optimisticallyRemoveRelation: (relationId: string, sourceId: string, targetId: string) => void;
	/**
	 * Trigger refetch for affected target + all sources (to refresh delegate counts / availability).
	 */
	sendUpdates(input: { relationTargetId: string; relations?: Relation[] }): void;
};

const Context = createContext<RepresentativesContext>(undefined);
Context.displayName = 'RepresentativesContext';
export function RepresentativesContextProvider({ children, unit }: RepresentativesContextProps) {
	const dataCacheRef = useRef<RepresentativesDataCache>({
		sourceData: {},
		targetData: {}
	});
	const [, forceUpdate] = useState({});

	// Track pending optimistic updates to avoid premature overwrites
	const pendingUpdatesRef = useRef<Set<string>>(new Set());

	/**We add the source organisation to the role string, because of this, it is hard to check whether a position has already been taken by a certain person.
	 * We need to be able to compare the actual role name (without the organisation name) to the position
	 */
	function getBasePosition(position: string) {
		if (strIsNullOrEmpty(position)) {
			return '';
		}
		if ((unit.positions?.length ?? 0) < 1) {
			return position;
		}
		const basePosition = unit.positions.find(p => position.includes(p));
		if (strIsNullOrEmpty(basePosition)) {
			return position;
		}
		return basePosition;
	}

	function getSourceData(sourceId: string) {
		return dataCacheRef.current.sourceData[sourceId];
	}

	function getTargetData(targetId: string) {
		return dataCacheRef.current.targetData[targetId];
	}

	function updateSourceData(sourceId: string, data: { data: EntityInfo[]; totalResults: number; sourceDelegateCaps?: DelegateCapInfo[] }) {
		// Don't overwrite if we have pending optimistic updates for this source
		const pendingKey = `source-${sourceId}`;
		if (pendingUpdatesRef.current.has(pendingKey)) {
			return false;
		}

		const existing = dataCacheRef.current.sourceData[sourceId];
		// Fast comparison using length and first few items
		if (existing && 
			existing.totalResults === data.totalResults &&
			existing.data.length === data.data.length &&
			existing.data.length > 0 &&
			existing.data[0]?.id === data.data[0]?.id &&
			existing.data[0]?.relations?.length === data.data[0]?.relations?.length) {
			return false; // No meaningful change
		}

		dataCacheRef.current.sourceData[sourceId] = data;
		forceUpdate({});
		return true;
	}

	function updateTargetData(targetId: string, data: { data: EntityInfo[]; totalResults: number }) {
		// Don't overwrite if we have pending optimistic updates for this target
		const pendingKey = `target-${targetId}`;
		if (pendingUpdatesRef.current.has(pendingKey)) {
			return false;
		}

		const existing = dataCacheRef.current.targetData[targetId];
		// Fast comparison using length and first few items
		if (existing && 
			existing.totalResults === data.totalResults &&
			existing.data.length === data.data.length &&
			existing.data.length > 0 &&
			existing.data[0]?.id === data.data[0]?.id &&
			existing.data[0]?.relations?.length === data.data[0]?.relations?.length) {
			return false; // No meaningful change
		}

		dataCacheRef.current.targetData[targetId] = data;
		forceUpdate({});
		return true;
	}

	function optimisticallyAddRelation(personId: string, relation: Relation, sourceId: string, targetId: string) {
		// Mark as pending to prevent server overwrites
		pendingUpdatesRef.current.add(`source-${sourceId}`);
		pendingUpdatesRef.current.add(`target-${targetId}`);

		// Update source data
		const sourceData = dataCacheRef.current.sourceData[sourceId];
		if (sourceData) {
			const personIndex = sourceData.data.findIndex(p => p.id === personId);
			if (personIndex !== -1) {
				sourceData.data[personIndex] = {
					...sourceData.data[personIndex],
					relations: [...(sourceData.data[personIndex].relations ?? []), relation]
				};
			}
		}

		// Update target data
		const targetData = dataCacheRef.current.targetData[targetId];
		if (targetData) {
			const existingPerson = targetData.data.find(p => p.id === personId);
			if (!existingPerson && sourceData) {
				// Add person to target if not already there
				const sourcePerson = sourceData.data.find(p => p.id === personId);
				if (sourcePerson) {
					targetData.data.push({
						...sourcePerson,
						relations: [relation]
					});
				}
			} else if (existingPerson) {
				// Update existing person in target
				const personIndex = targetData.data.findIndex(p => p.id === personId);
				targetData.data[personIndex] = {
					...existingPerson,
					relations: [...(existingPerson.relations ?? []), relation]
				};
			}
		}

		forceUpdate({});
	}

	function optimisticallyRemoveRelation(relationId: string, sourceId: string, targetId: string) {
		// Mark as pending to prevent server overwrites
		pendingUpdatesRef.current.add(`source-${sourceId}`);
		pendingUpdatesRef.current.add(`target-${targetId}`);

		// Update source data
		const sourceData = dataCacheRef.current.sourceData[sourceId];
		if (sourceData) {
			sourceData.data.forEach(person => {
				if (person.relations) {
					person.relations = person.relations.filter(r => r.relationId !== relationId);
				}
			});
		}

		// Update target data
		const targetData = dataCacheRef.current.targetData[targetId];
		if (targetData) {
			targetData.data.forEach(person => {
				if (person.relations) {
					person.relations = person.relations.filter(r => r.relationId !== relationId);
				}
			});
			// Remove person from target if they have no more relations to this target
			targetData.data = targetData.data.filter(
				person => person.relations && person.relations.some(r => r.relationTargetId === targetId)
			);
		}

		forceUpdate({});
	}

	/**Call this after removing/adding people to ensure target + all sources refresh.
	 * Delay the refetch to allow server processing time. */
	function sendUpdates(input: { relationTargetId: string; relations?: Relation[] }) {
		const { relationTargetId } = input;

		// Delay refetch to give server time to process the mutation
		setTimeout(() => {

			// Clear pending updates to allow fresh data
			pendingUpdatesRef.current.clear();

			if (relationTargetId) {
				sendUpdate(representativeEntityId({ unitId: unit.id, entityId: relationTargetId, type: 'target' }));
			}
			// Always refresh all sources so their delegate counts / availability recalc
			for (const source of unit.sourceEntities ?? []) {
				sendUpdate(representativeEntityId({ unitId: unit.id, entityId: source.id, type: 'source' }));
			}
		}, 3000); // 3 second delay to allow server processing
	}

	const contextValue: RepresentativesContext = {
		unit,
		getBasePosition,
		getSourceData,
		getTargetData,
		updateSourceData,
		updateTargetData,
		optimisticallyAddRelation,
		optimisticallyRemoveRelation,
		sendUpdates
	};

	return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

export function useRepresentativesContext() {
	const context = useContext(Context);

	if (!context) throw new Error('Missing representatives provider context');

	return context as RepresentativesContext;
}
